import * as React from "react";
import {
  PrefetchPageLinks,
  useNavigate,
  useRouteLoaderData,
} from "react-router";

import { compileLegacyRoutePatterns } from "~/hec/legacy-routes";

interface RootLoaderShape {
  legacyRoutePatterns?: readonly string[];
}

/**
 * Prefetch + SPA-delegate the plain `<a href>` links Builder renders inside CMS
 * content (rich text, content cards, embedded HTML) that we can't wrap in a
 * `<HybridLink>`. (SFCC-11297.)
 *
 * Modeled on remix-utils' `PrefetchPageAnchors`, adapted for the hybrid SFRA
 * app on two points:
 *
 *  1. **Legacy-aware click delegation.** Same-origin anchors are turned into
 *     SPA navigations so the prefetched `.data` is actually used (a plain `<a>`
 *     full-document click never consumes a `.data` prefetch). SFRA-owned routes
 *     (`legacyRoutePatterns`, the same source `<HybridLink>` uses) are skipped
 *     so they keep their full-document nav and `worker.server.ts:proxyToSFRA`
 *     can serve them. remix-utils delegates *every* same-host anchor, which
 *     would break those hybrid routes.
 *  2. **Intent prefetch** via delegated `pointerover` / `focusin` /
 *     `touchstart` — the same triggers React Router's
 *     `<Link prefetch="intent">` uses. This surface originally prefetched on
 *     viewport (IntersectionObserver), but on content-dense pages every
 *     in-viewport anchor fired at hydration in a single burst — ~25
 *     simultaneous `.data` Worker invocations per desktop homepage load (the
 *     nav prefetch storm; canceled prefetches are also the documented wedge
 *     precursor in `docs/oom-hung-request-investigation.md`). Intent keeps
 *     the click fast — the prefetch starts on hover/touchstart and the
 *     response is browser-cacheable via `PrefetchCacheMiddleware`.
 *
 * The wrapper is `display: contents` so it adds no box to the Builder layout.
 */
export function PrefetchBuilderAnchors({
  children,
}: {
  children: React.ReactNode;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const data = useRouteLoaderData("root") as RootLoaderShape | undefined;
  const [pages, setPages] = React.useState<string[]>([]);

  // Same-origin, internal (non-SFRA), navigable anchor? Mirrors the
  // `<HybridLink>` legacy check so we never SPA into a proxied SFRA route.
  const isCandidate = React.useMemo(() => {
    const patterns = compileLegacyRoutePatterns(data?.legacyRoutePatterns);
    return (a: HTMLAnchorElement | null): a is HTMLAnchorElement => {
      if (!a || !a.hasAttribute("href")) return false;
      const raw = a.getAttribute("href") ?? "";
      // Fragment / mailto: / tel: — leave to the browser.
      if (raw.startsWith("#") || a.host !== window.location.host) return false;
      if (a.hasAttribute("download")) return false;
      if (a.target && a.target !== "_self") return false;
      // SFRA-owned route -> must full-document nav (worker proxies to SFRA).
      if (patterns.some(p => p.test(a.pathname))) return false;
      return true;
    };
  }, [data?.legacyRoutePatterns]);

  // Click delegation -> SPA navigation for internal, non-legacy anchors.
  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
        return;
      }
      if (!(event.target instanceof HTMLElement)) return;
      const anchor = event.target.closest("a");
      if (!isCandidate(anchor)) return;
      navigate({
        pathname: anchor.pathname,
        search: anchor.search,
        hash: anchor.hash,
      });
      event.preventDefault();
    };
    node.addEventListener("click", handleClick);
    return () => node.removeEventListener("click", handleClick);
  }, [isCandidate, navigate]);

  // Prefetch an internal anchor's page on interaction intent. Delegated, so
  // anchors Builder mounts/replaces after first paint need no rescan.
  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    // Session-local dedupe: `pages` is append-only state, so re-firing for a
    // page already queued would only churn state. The functional-update
    // `includes` guard below also protects against a stale `seen` when this
    // effect re-runs (new Set) after `isCandidate` changes.
    const seen = new Set<string>();
    const handleIntent = (event: Event) => {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest("a");
      if (!isCandidate(anchor)) return;
      const page = anchor.pathname + anchor.search;
      if (seen.has(page)) return;
      seen.add(page);
      setPages(prev => (prev.includes(page) ? prev : [...prev, page]));
    };

    node.addEventListener("pointerover", handleIntent);
    node.addEventListener("focusin", handleIntent);
    node.addEventListener("touchstart", handleIntent, { passive: true });
    return () => {
      node.removeEventListener("pointerover", handleIntent);
      node.removeEventListener("focusin", handleIntent);
      node.removeEventListener("touchstart", handleIntent);
    };
  }, [isCandidate]);

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      {children}
      {pages.map(page => (
        <PrefetchPageLinks key={page} page={page} />
      ))}
    </div>
  );
}
